import React, { useState, useRef, useEffect } from "react";
import CountrySelectSubcriber from "./CountrySelectSubcriber";
import ReCaptchaDemo from "../ReCaptchaDemo";
import BASE_URL from "../../config";

const SubscribeForm = ({ closeModal, showMessage }) => {
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [city, setCity] = useState("");
  const [country, setCountry] = useState("");
  const [organization, setOrganization] = useState("");
  const [organizationType, setOrganizationType] = useState("");
  const [stateProvince, setStateProvince] = useState("");
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [message, setMessage] = useState("");
  const [captchaToken, setCaptchaToken] = useState("");
  const [messageType, setMessageType] = useState(""); // 'success' or 'error'
  const recaptchaRef = useRef(null);
  const [errors, setErrors] = useState(""); // State for error message


  const organizationOptions = [
    "Primary/Composite School",
    "Middle School",
    "Secondary School",
    "Senior School",
    "Govt Educational Institute",
    "Non-Govt Educational Institute",
    "Research Organization",
    "Foundation",
    "Government",
    "Media/Press",
    // "Multilateral/Intergovernmental",
    "NGO",
    "Private for Profit",
    "Non-University Research",
    "University",
  ];

  // Function to handle text input and prevent special characters
  const handleTextChange = (setter, fieldName) => (e) => {
    const value = e.target.value;
    setter(value); // Always update field

    const isRequired = requiredFields.has(fieldName);
    const trimmedValue = value.trim();

    // ✅ Only validate empty value for required fields
    if (isRequired && trimmedValue === "") {
      setErrors((prev) => ({
        ...prev,
        [fieldName]: "This field is required.",
      }));
      return;
    }

    if (fieldName === "email") {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value)) {
        setErrors((prev) => ({
          ...prev,
          [fieldName]: "Please enter a valid email address.",
        }));
      } else {
        setErrors((prev) => ({ ...prev, [fieldName]: "" }));
      }
    } else if (/[^A-Za-z\s.]/.test(value)) {
      // ✅ Apply special character check only for required fields (or optionally for all, up to you)
      setErrors((prev) => ({
        ...prev,
        [fieldName]: "Special characters are not allowed!",
      }));
    } else {
      setErrors((prev) => ({ ...prev, [fieldName]: "" }));
    }
  };

  const requiredFields = new Set(["email", "firstName", "lastName"]);
  const [touchedFields, setTouchedFields] = useState({});

  const handleBlur = (e, fieldName) => {
    const value = e.target?.value?.toString() || "";
    const trimmedValue = value.trim();
    const isRequired = requiredFields.has(fieldName);

    // Update touched state
    setTouchedFields((prev) => ({ ...prev, [fieldName]: true }));

    setErrors((prev) => {
      const newErrors = { ...prev };

      // ✅ Only validate required fields
      if (isRequired) {
        if (trimmedValue === "") {
          newErrors[fieldName] = "This field is required.";
        } else if (fieldName !== "email" && trimmedValue.length < 2) {
          newErrors[fieldName] = "At least 2 characters are required.";
        } else {
          newErrors[fieldName] = "";
        }
      } else {
        // ✅ Optional fields: Never show errors
        newErrors[fieldName] = "";
      }

      return newErrors;
    });
  };




  useEffect(() => {
    // Stop background scrolling when modal is open
    document.body.style.overflow = "hidden";
    return () => {
      // Allow scrolling when modal is closed
      document.body.style.overflow = "";
    };
  }, []);

  const getCsrfToken = async () => {
    try {
      const response = await fetch(
        `${BASE_URL}/session/token?_format=json`
      );
      if (!response.ok) throw new Error("Failed to retrieve CSRF token");
      return await response.text();
    } catch (error) {
      console.error("Error fetching CSRF token:", error);
      throw error;
    }
  };

  const handleCaptchaVerify = (token) => {
    setCaptchaToken(token); // Save token when CAPTCHA is verified
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setMessage("");
    setMessageType("");

    if (!email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      alert("Error: Please enter a valid email address.");
      setLoading(false);
      return;
    }
    if (!firstName.trim() || firstName.trim().length < 2 || !/^[a-zA-Z\s.]+$/.test(firstName)) {
      alert("Error: First name must be at least 2 characters and contain letters only.");
      setLoading(false);
      return;
    }

    if (!lastName.trim() || lastName.trim().length < 2 || !/^[a-zA-Z\s]+$/.test(lastName)) {
      alert("Error: Last name must be at least 2 characters and contain letters only.");
      setLoading(false);
      return;
    }

    try {
      const csrfToken = await getCsrfToken();

      const checkResponse = await fetch(
        `${BASE_URL}/jsonapi/simplenews_subscriber/simplenews_subscriber?filter[mail]=${email}`,
        { method: "GET", headers: { Accept: "application/vnd.api+json" } }
      );

      const checkData = await checkResponse.json();
      const existingSubscriber = checkData?.data?.[0];

      if (existingSubscriber) {
        const subscriberId = existingSubscriber.id;
        const currentStatus = existingSubscriber.attributes.status;

        if (!currentStatus) {
          const resubscribeData = {
            data: {
              type: "simplenews_subscriber--simplenews_subscriber",
              id: subscriberId,
              attributes: { status: true },
            },
          };

          await fetch(
            `${BASE_URL}/jsonapi/simplenews_subscriber/simplenews_subscriber/${subscriberId}`,
            {
              method: "PATCH",
              headers: {
                "Content-Type": "application/vnd.api+json",
                Accept: "application/vnd.api+json",
                "X-CSRF-Token": csrfToken,
              },
              body: JSON.stringify(resubscribeData),
            }
          );

          showMessage("Welcome back! Your subscription is reactivated.", "success");
        } else {
          showMessage("You are already subscribed.", "error");
        }
      } else {
        const requestData = {
          mail: [{ value: email }],
          field_first_name: [{ value: firstName }],
          field_last_name: [{ value: lastName }],
          langcode: [{ value: "en" }],
          status: [{ value: 1 }],
          subscriptions: [
            { target_id: "default", target_type: "simplenews_newsletter", target_uuid: "31c49d5f-69ea-4377-835e-f9f447b2c5e3" },
            { target_id: "press_releases", target_type: "simplenews_newsletter", target_uuid: "d95a9a8b-9bbf-460d-9f33-b987d714180d" },
          ],
          field_city: city ? [{ value: city }] : [],
          field_country: country ? [{ value: country }] : [],
          field_organization: organization ? [{ value: organization }] : [],
          field_organization_type: organizationType ? [{ value: organizationType }] : [],
          field_state_province_district: stateProvince ? [{ value: stateProvince }] : [],
        };

        const response = await fetch(
          `${BASE_URL}/entity/simplenews_subscriber?_format=json`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
              "X-CSRF-Token": csrfToken,
            },
            body: JSON.stringify(requestData),
          }
        );

        if (!response.ok) {
          const errorText = await response.text();
          if (errorText.includes("already exists")) {
            showMessage("You are already subscribed.", "error");
          } else {
            throw new Error("Failed to subscribe. " + errorText);
          }
        } else {
          showMessage("Subscription successful!", "success");
        }
      }

      // ✅ Close the modal immediately
      closeModal();

      // ✅ Keep the success message visible for 5 seconds
      setTimeout(() => {
        setMessage("");
        setMessageType("");
      }, 9000);

      // ✅ Reset form fields
      setEmail("");
      setFirstName("");
      setLastName("");
      setCity("");
      setCountry("");
      setOrganization("");
      setOrganizationType("");
      setStateProvince("");
      setCaptchaToken("");

      // ✅ Reset reCAPTCHA
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }

    } catch (error) {
      showMessage(`Error: ${error.message}`, "error");
    } finally {
      setLoading(false);
    }
  };



  return (
    // <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
    <div className="bg-white font-body mt-12 w-full max-w-lg p-2 rounded-lg shadow-lg relative">
      <div className="flex items-center justify-between border-b pb-2">
        <h3 className="text-lg text-indian-red text-center font-bold">Shiksha Newsletter Subscription</h3>
        {/* <button
          type="button"
          onClick={closeModal}
          className="text-black hover:text-white bg-transparent hover:bg-red-600 rounded-lg text-lg w-10 h-10 flex justify-center items-center"
        >
          <span aria-hidden="true" className="text-2xl">&times;</span>
        </button> */}
      </div>

      <div className="w-full max-w-lg mx-auto mt-2 px-4  mb-2">
        <form onSubmit={handleSubmit} className="grid grid-cols-2 sm:grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="email" className="block text-gray-700 text-sm font-bold mb-2">
              Email<span className="text-red-500 ml-1">*</span>
            </label>
            <input
              type="email"
              placeholder="Enter your Email"
              value={email}
              onChange={handleTextChange(setEmail, "email")}
              onBlur={(e) => handleBlur(e, "email")}
              required
              className="block w-full rounded-md border-0 px-2 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-gray-100 sm:text-sm sm:leading-6"
            />
            {errors.email && <p className="text-red-500 text-sm">{errors.email}</p>}
          </div>
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2">
              First Name<span className="text-red-500 ml-1">*</span>
            </label>
            <input
              type="text"
              placeholder="Enter your First Name"
              value={firstName}
              onChange={handleTextChange(setFirstName, "firstName")}
              onBlur={(e) => handleBlur(e, "firstName")}
              required
              className="block w-full rounded-md border-0 px-2 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-gray-100 sm:text-sm sm:leading-6"
            />
            {errors.firstName && <p className="text-red-500 text-sm">{errors.firstName}</p>}
          </div>

          {/* Last Name */}
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2">
              Last Name<span className="text-red-500 ml-1">*</span>
            </label>
            <input
              type="text"
              placeholder="Last Name"
              value={lastName}
              onChange={handleTextChange(setLastName, "lastName")}
              onBlur={(e) => handleBlur(e, "lastName")}
              required
              className="block w-full rounded-md border-0 px-2 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-gray-100 sm:text-sm sm:leading-6"
            />
            {errors.lastName && <p className="text-red-500 text-sm">{errors.lastName}</p>}
          </div>
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2">
              City
            </label>
            <input
              type="text"
              placeholder="City"
              value={city}
              onChange={handleTextChange(setCity, "city")}
              // onBlur={(e) => handleBlur(e, "city")}
              className="block w-full rounded-md border-0 px-2 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-gray-100 sm:text-sm sm:leading-6"
            />
            {errors.city && <p className="text-red-500 text-sm">{errors.city}</p>}
          </div>

          {/* Organization Name */}
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2">
              Organization Name
            </label>
            <input
              type="text"
              placeholder="Organization Name"
              value={organization}
              onChange={handleTextChange(setOrganization, "organization")}
              onBlur={(e) => handleBlur(e, "organization")}
              className="block w-full rounded-md border-0 px-2 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-gray-100 sm:text-sm sm:leading-6"
            />
            {errors.organization && <p className="text-red-500 text-sm">{errors.organization}</p>}
          </div>

          {/* State/Province/District */}
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2">
              State/Province/District
            </label>
            <input
              type="text"
              placeholder="State/Province/District"
              value={stateProvince}
              onChange={handleTextChange(setStateProvince, "stateProvince")}
              className="block w-full rounded-md border-0 px-2 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-gray-100 sm:text-sm sm:leading-6"
            />
            {errors.stateProvince && <p className="text-red-500 text-sm">{errors.stateProvince}</p>}
          </div>

          <div>
            <CountrySelectSubcriber
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              className="block w-full rounded-md border-0 px-2 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-gray-100 sm:text-sm sm:leading-6"
            />
          </div>
          <div>
            <label htmlFor="type" className="block text-gray-700 text-sm font-bold mb-2">
              Select Organization Type
            </label>
            <select
              value={organizationType}
              onChange={(e) => setOrganizationType(e.target.value)}
              className="block w-full rounded-md border-0 px-1.5 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-gray-100 sm:text-sm sm:leading-6"
            >
              <option value="">Select Organization Type</option>
              {organizationOptions.map((type, index) => (
                <option key={index} value={type}>{type}</option>
              ))}
            </select>
          </div>
          <ReCaptchaDemo ref={recaptchaRef} onValidate={handleCaptchaVerify} />
          <br />
          {/* <div className="sm:col-span-2 flex justify-between gap-4 mt-4"> */}
          <button
            type="button"
            className="bg-red-500 hover:bg-red-700 text-white font-bold my-4 mx-4 py-2 px-4 rounded focus:outline-none focus:shadow-outline"
            onClick={closeModal}
          >
            &times; Close
          </button>
          <button
            type="submit"
            className={`bg-blue-500 text-white font-bold my-4 mx-4 py-2 px-4 rounded focus:outline-none focus:shadow-outline${captchaToken ? "" : " opacity-50 cursor-not-allowed"}`}
            disabled={!captchaToken}
          >
            Subscribe
          </button>
          {/* </div> */}


        </form>
      </div>
      {message && (
        <p className={`p-4 text-center font-semibold ${messageType === "success" ? "text-green-900" : "text-red-600"}`}>
          {message}
        </p>
      )}
    </div>
    // </div>


  );
};

export default SubscribeForm;
